import React, { useEffect, useState } from "react";
import { startGuidedTour } from "./GuidedTour";
import "./ContextualHelp.scss";

const ArticleView = ({ article, onBack }) => (
  <>
    <button type="button" className="contextual-help__back" onClick={onBack}>
      <i className="bx bx-arrow-back" /> Back to help
    </button>
    <article className="contextual-help__article">
      <span>{article.contextTitle}</span>
      <h3>{article.title}</h3>
      {article.body.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </article>
  </>
);

const HelpDrawer = ({
  context,
  isOpen,
  onClose,
  onPiersTour,
  onGettingStarted,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [article, setArticle] = useState(null);

  useEffect(() => {
    setActiveTab("overview");
    setArticle(null);
  }, [context.id, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const bodyPaddingRight =
      Number.parseFloat(window.getComputedStyle(document.body).paddingRight) ||
      0;

    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0 && previousOverflow !== "hidden") {
      document.body.style.paddingRight = `${
        bodyPaddingRight + scrollbarWidth
      }px`;
    }

    return () => {
      if (previousOverflow) document.body.style.overflow = previousOverflow;
      else document.body.style.removeProperty("overflow");
      if (previousPaddingRight)
        document.body.style.paddingRight = previousPaddingRight;
      else document.body.style.removeProperty("padding-right");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const openArticle = (selectedArticle, owner = context) => {
    setArticle({ ...selectedArticle, contextTitle: owner.title });
  };

  const startTour = () => {
    onClose();
    window.setTimeout(() => startGuidedTour(context.tourSteps), 120);
  };

  const runPiersTour = () => {
    onClose();
    window.setTimeout(onPiersTour, 120);
  };

  const showGettingStarted = () => {
    onClose();
    window.setTimeout(onGettingStarted, 120);
  };

  const groupedArticles = context.articles.reduce((groups, item) => {
    groups[item.group] = groups[item.group] || [];
    groups[item.group].push(item);
    return groups;
  }, {});

  return (
    <div className="contextual-help-overlay">
      <aside
        className="contextual-help"
        role="dialog"
        aria-label="Contextual help"
      >
        <header className="contextual-help__header">
          <h2>Help</h2>
          <div className="contextual-help__header-actions">
            <button type="button" onClick={onClose} aria-label="Close help">
              <i className="bx bx-x" />
            </button>
          </div>
        </header>

        <section className="contextual-help__topic">
          <span className="contextual-help__topic-icon">
            <i className={`bx ${context.icon}`} />
          </span>
          <div>
            <h3>{context.title}</h3>
            <p>{context.description}</p>
          </div>
        </section>

        <section className="contextual-help__getting-started">
          <div>
            <span>
              <i className="bx bx-compass" />
            </span>
            <div>
              <h3>Getting Started</h3>
              <p>Learn the main areas of the PIERS workspace.</p>
            </div>
          </div>
          <button type="button" onClick={showGettingStarted}>
            Getting Started
          </button>
          <button type="button" onClick={runPiersTour}>
            Take PIERS Tour
          </button>
          <a href="/help" target="_blank" rel="noreferrer">
            Help &amp; Documentation <i className="bx bx-link-external" />
          </a>
        </section>

        <nav className="contextual-help__tabs" aria-label="Help sections">
          <button
            type="button"
            className={activeTab === "overview" ? "is-active" : ""}
            onClick={() => {
              setActiveTab("overview");
              setArticle(null);
            }}
          >
            Overview
          </button>
          <button
            type="button"
            className={activeTab === "how-to" ? "is-active" : ""}
            onClick={() => {
              setActiveTab("how-to");
              setArticle(null);
            }}
          >
            How-to
          </button>
        </nav>

        <main className="contextual-help__content">
          {article ? (
            <ArticleView article={article} onBack={() => setArticle(null)} />
          ) : activeTab === "overview" ? (
            <>
              <section className="contextual-help__section">
                <h3>About this page</h3>
                <p>{context.about}</p>
              </section>
              <section className="contextual-help__section">
                <h3>What you can do</h3>
                <ul>
                  {context.capabilities.map((item) => (
                    <li key={item}>
                      <i className="bx bx-check-circle" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
              <section className="contextual-help__section">
                <h3>How do I...</h3>
                <div className="contextual-help__links">
                  {context.articles.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => openArticle(item)}
                    >
                      {item.title}
                      <i className="bx bx-chevron-right" />
                    </button>
                  ))}
                </div>
              </section>
              <section className="contextual-help__tour">
                <span>
                  <i className="bx bx-compass" />
                </span>
                <div>
                  <h3>Take a Guided Tour</h3>
                  <p>Walk through this screen to learn how it works.</p>
                  <button type="button" onClick={startTour}>
                    <i className="bx bx-play" />
                    Start Tour
                  </button>
                </div>
              </section>
            </>
          ) : (
            Object.entries(groupedArticles).map(([group, articles]) => (
              <section className="contextual-help__section" key={group}>
                <h3>{group}</h3>
                <div className="contextual-help__links">
                  {articles.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={() => openArticle(item)}
                    >
                      {item.title}
                      <i className="bx bx-chevron-right" />
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}
        </main>

        <footer className="contextual-help__search">
          <span>Need more help?</span>
          <a
            href="/help"
            target="_blank"
            rel="noreferrer"
            aria-label="Open PIERS Help Center"
            title="Open PIERS Help Center"
          >
            <i className="bx bx-link-external" />
          </a>
        </footer>
      </aside>
    </div>
  );
};

export default HelpDrawer;
